import {
  getAllCompanies,
  getCompaniesByIDs,
  getCompanyById,
  getCompanyUsers,
  getUserCompanies,
  getUserJoinedCompanies,
  validateCompanyInvitation,
} from '@/services/companies'
import type { Company } from '@/types'
import type { CompanyUser } from '@/types/companyUser'
import { type UseQueryOptions, useQuery } from '@tanstack/react-query'
import { companyKeys } from './query-keys'
import type { AnalyticsDataItem, ChartDataItem } from './types'
import { companyUserUtils, companyUtils } from './utils'

export const useCompany = (
  companyId: string,
  options?: Omit<UseQueryOptions<Company, Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: companyKeys.detail(companyId),
    queryFn: () => getCompanyById(companyId),
    enabled: !!companyId,
    ...options,
  })
}

export const useAllCompanies = (
  maxItems = 20,
  options?: Omit<UseQueryOptions<Company[], Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: companyKeys.list({ maxItems }),
    queryFn: () => getAllCompanies(maxItems),
    ...options,
  })
}

export const useActiveCompanies = (
  maxItems = 20,
  options?: Omit<UseQueryOptions<Company[], Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: companyKeys.list({ maxItems, status: 'active' }),
    queryFn: () => getAllCompanies(maxItems),
    select: companyUtils.filterActiveCompanies,
    ...options,
  })
}

export const useTopCompaniesByUsers = (
  maxItems = 20,
  limit = 5,
  options?: Omit<UseQueryOptions<Company[], Error, ChartDataItem[]>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  return useQuery({
    queryKey: companyKeys.list({ maxItems, sorted: 'userCount', limit }),
    queryFn: () => getAllCompanies(maxItems),
    select: (data: Company[]) => companyUtils.getTopCompaniesByUsers(data, limit),
    ...options,
  })
}

export const useCompaniesAnalytics = (
  maxItems = 100,
  options?: Omit<UseQueryOptions<Company[], Error, AnalyticsDataItem[]>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  return useQuery({
    queryKey: companyKeys.list({ maxItems, analytics: true }),
    queryFn: () => getAllCompanies(maxItems),
    select: companyUtils.toAnalyticsData,
    ...options,
  })
}

export const useFilteredCompanies = (
  searchTerm: string,
  companiesWithOwners: any[],
  options?: Omit<UseQueryOptions<any[], Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: companyKeys.list({ searchTerm, filtered: true }),
    queryFn: () => Promise.resolve(companiesWithOwners),
    select: (data) => companyUtils.filterBySearchTerm(data, searchTerm),
    enabled: !!companiesWithOwners?.length,
    ...options,
  })
}

export const useCompaniesByIds = (
  companyIds: string[],
  options?: Omit<UseQueryOptions<Company[], Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: companyKeys.byIds(companyIds),
    queryFn: () => getCompaniesByIDs(companyIds),
    enabled: companyIds.length > 0,
    ...options,
  })
}

export const useCompanyUsers = (
  companyId: string,
  options?: Omit<UseQueryOptions<CompanyUser[], Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: companyKeys.companyUsers(companyId),
    queryFn: () => getCompanyUsers(companyId),
    enabled: !!companyId,
    ...options,
  })
}

export const useCompanyUserIds = (
  companyId: string,
  options?: Omit<UseQueryOptions<CompanyUser[], Error, string[]>, 'queryKey' | 'queryFn' | 'select'>,
) => {
  return useQuery({
    queryKey: companyKeys.companyUsers(companyId),
    queryFn: () => getCompanyUsers(companyId),
    select: companyUserUtils.extractUserIds,
    enabled: !!companyId,
    ...options,
  })
}

export const useUserJoinedCompanies = (
  userId: string,
  options?: Omit<UseQueryOptions<CompanyUser[], Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: companyKeys.userJoinedCompanies(userId),
    queryFn: () => getUserJoinedCompanies(userId),
    enabled: !!userId,
    ...options,
  })
}

export const useUserCompanies = (
  userId: string,
  options?: Omit<
    UseQueryOptions<{ companies: Company[]; roles: Map<string, CompanyUser> }, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: companyKeys.userCompanies(userId),
    queryFn: () => getUserCompanies(userId),
    enabled: !!userId,
    ...options,
  })
}

export const useCompanyInvitation = (
  token: string,
  options?: Omit<
    UseQueryOptions<
      {
        companyId: string
        companyName: string
        inviteToken: string
        pendingOwnerEmail: string
        inviteExpiresAt: Date
        isValid: boolean
        isExpired: boolean
      },
      Error
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: companyKeys.invitation(token),
    queryFn: () => validateCompanyInvitation(token),
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}
